/* eslint-disable no-unused-vars */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import {
    AccountBalance as AccountBalanceIcon,
    CheckCircle as CheckCircleIcon,
    Verified as VerifiedIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Home as HomeIcon,
    Groups as GroupsIcon,
    SwapHoriz as SwapHorizIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import Teachr from '../../../Objects/models/Teachr';
import TeachrRepository from '../../../Repositories/TeachrRepository';
import Functions from '../../../Helpers/Functions';
import { userSetCurrentProcuration, userSetInfoSiret } from '../../../Redux/Actions/UserActions';
import Sponsorship from '../../../Objects/models/Sponsorship';
import { store } from '../../../Redux/store';
import { putSponsorship } from '../../../Redux/Actions/SponsorshipActions';
import { PutSponsorship } from '../../../Objects/interfaces/AppInterface';
import { ENV_DECLARONS, ENV_VERIFIED_TRAINER, getActiveBrandConfig, getActivePrestationBrandConfig, isBrandActive } from '../../../config/environment.config';
import useTailwindBrand from '../../../Theme/useTailwindBrand';
import { useProfilContext } from '../../../Contexts/ProfilContexte';
import ButtonField from '../../../Components/Ui/ButtonField';
import FormSelect from '../../../Components/FormFields/FormSelect';
import Settings from '../../../Components/Settings';
import FormInput from '../../../Components/Ui/FormInput';
import ValidateSiret from '../../../Validation/Siret';
import { StateInterface } from '../CompletProfilStep';


const URSSAF_MANAGE_BUSINESS_URL = 'https://www.autoentrepreneur.urssaf.fr/portail/accueil/gerer-mon-auto-entreprise.html';
const URSSAF_AUTO_ENTREPRENEUR_URL = 'https://procedures.inpi.fr/?/';
export const REQUIRED_APE_CODES = ['8559B', '8552Z', '85.59B', '85.52Z'];
export const DECLARONS_REQUIRED_APE_CODES = [
    '4321A', '4322A', '4932Z', '5320Z', '5621Z', '6202B', '6209Z', '7430Z',
    '8121Z', '8130Z', '8299Z', '8551Z', '8559B', '8810A', '8891A', '9001Z',
    '9329Z', '9511Z', '9609Z', '9602B', '9602A',
    '43.21A', '43.22A', '49.32Z', '53.20Z', '56.21Z', '62.02B', '62.09Z', '74.30Z',
    '81.21Z', '81.30Z', '82.99Z', '85.51Z', '85.59B', '88.10A', '88.91A', '90.01Z',
    '93.29Z', '95.11Z', '96.09Z', '96.02B', '96.02A',
];

const useBusinessTypeSelection = (teachr: Teachr, nextStep: (step: number) => void, setState: React.Dispatch<React.SetStateAction<StateInterface>>) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const siretInfo = useSelector((state: any) => state.user?.siretInfo);
    const [selectedBusinessTypeIndex, setSelectedBusinessTypeIndex] = useState<number | undefined>();
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [isProcessingProcuration, setIsProcessingProcuration] = useState(false);
    const [siret, setSiret] = useState(teachr?.RCS || '');
    const [errorSiret, setErrorSiret] = useState(false);
    const [siretApiError, setSiretApiError] = useState<string | undefined>(undefined);
    const [siretVerified, setSiretVerified] = useState(teachr?.RCS ? true : false);
    const [siretVerifying, setSiretVerifying] = useState(false);
    const [homeCoursesAnswer, setHomeCoursesAnswer] = useState<boolean | null>(null);
    const [showApeSuccess, setShowApeSuccess] = useState(false);
    const [showApeFailed, setShowApeFailed] = useState(false);

    useEffect(() => {
        if (siretVerified && teachr?.scoop) {
            setHomeCoursesAnswer(false);
        }
    }, [siretVerified, teachr?.scoop]);

    const businessTypeOptions = useMemo(() => {
        if(isBrandActive(ENV_VERIFIED_TRAINER)) {
            return Functions.businessTypeListVt.map((item) => item.label);
        } else {
            return Functions.businessTypeList.map((item) => item.label);
        }
    }, []);

    useEffect(() => {
        if (teachr && businessTypeOptions.length > 0) {
            const currentTeachr = teachr;
            if (currentTeachr?.businessType && currentTeachr?.businessType !== '') {
                const businessTypeLabel = Functions.renderBusinessType(currentTeachr?.businessType);
                const index = businessTypeOptions.indexOf(businessTypeLabel);
                if (index !== -1) setSelectedBusinessTypeIndex(index);
            }
        }
    }, [businessTypeOptions, teachr]);

    const hasSelectedBusinessType = selectedBusinessTypeIndex !== undefined && (isBrandActive(ENV_VERIFIED_TRAINER) ? selectedBusinessTypeIndex >= 0 : selectedBusinessTypeIndex > 0);
    const hasNoBusiness = selectedBusinessTypeIndex === 0;


    const handleProcurationForAutoEntrepreneur = useCallback(async () => {
        const currentTeachr = teachr;
        const handleNavigateToProcuration = async () => {
            if (!currentTeachr) return;
            try {
                const businessTypeValue = selectedBusinessTypeIndex && selectedBusinessTypeIndex > 0 ? Functions.businessTypeList[selectedBusinessTypeIndex].value : 'NOAE';
                const updatedTeachr = prepareTeachrForUpdate(currentTeachr, { businessType: businessTypeValue, procuration: '30' });
                await updateTeachrData(updatedTeachr, dispatch);
                dispatch(userSetCurrentProcuration(30));
                navigate(`/${getActiveBrandConfig()}/tableau-de-bord/procuration`);
            } catch (e) {
                console.error('Erreur lors de la mise à jour teachr.procuration:', e);
            }
        };

        if (currentTeachr?.sponsorship) {
            setIsProcessingProcuration(true);
            const sponsorship = new Sponsorship(currentTeachr.sponsorship);
            sponsorship.statut = 'PROC_AE';
            const data: PutSponsorship = {
                teachr: currentTeachr,
                sponsorship,
                setLoad: setIsProcessingProcuration,
                handleNext: handleNavigateToProcuration
            };
            store.dispatch(putSponsorship(data));
        } else {
            await handleNavigateToProcuration();
        }
    }, [teachr, dispatch, navigate, selectedBusinessTypeIndex]);

    const handleSelectBusinessType = useCallback((valueFromSelect: number) => {
        setSelectedBusinessTypeIndex(valueFromSelect - 1);
        if (valueFromSelect) setState(prev => ({ ...prev, businessType: Functions.businessTypeList[valueFromSelect - 1].value, hasAe: true }));
        setIsAccordionOpen(false);
        setSiret('');
        setErrorSiret(false);
        setSiretVerified(false);
        setHomeCoursesAnswer(null);
        setShowApeSuccess(false);
        setShowApeFailed(false);
    }, [setSelectedBusinessTypeIndex, setIsAccordionOpen, setState]);

    const handleGoBack = useCallback(() => {
        setSelectedBusinessTypeIndex(undefined);
    }, [setSelectedBusinessTypeIndex]);

    const handleVerifySiret = async () => {
        const validation = ValidateSiret(siret);
        if (!validation.valid) {
            setErrorSiret(true);
            return;
        }
        setSiretVerifying(true);
        setErrorSiret(false);
        try {
            const response = await TeachrRepository.getInfoBySiret(siret);
            const businessTypeValue = selectedBusinessTypeIndex && selectedBusinessTypeIndex > 0 ? Functions.businessTypeList[selectedBusinessTypeIndex].value : 'NOAE';
            if (response) {
                dispatch(userSetInfoSiret(response));
                if (response.header.statut === 200) {
                    setSiretVerified(true);
                    setSiretApiError(undefined);
                    const updatedTeachr = prepareTeachrForUpdate(teachr, { RCS: siret, businessType: businessTypeValue });
                    await updateTeachrData(updatedTeachr, dispatch);
                } else {
                    setSiretVerified(false);
                    setSiretApiError(`${response.header.message}, veuillez renseigner le bon siret`);
                }
            }
        } catch {
            setSiretVerified(false);
            setSiretApiError('Une erreur est survenue lors de la vérification.');
        } finally {
            setSiretVerifying(false);
        }
    };

    const handleProcurationSapClick = () => {
        setHomeCoursesAnswer(true);
        const apeCode = siretInfo?.etablissement?.uniteLegale?.activitePrincipaleUniteLegale;
        const validApeCodes = isBrandActive(ENV_DECLARONS) ? DECLARONS_REQUIRED_APE_CODES : REQUIRED_APE_CODES;
        if (validApeCodes.includes(apeCode)) {
            setShowApeSuccess(true);
        } else {
            setShowApeFailed(true);
        }
    };

    const handleGoToProcuration = () => {
        nextStep(1);
    };

    const handleCooperative = async () => {
        try {

            const updatedTeachr = prepareTeachrForUpdate(teachr, { scoop: '10', procuration: '10' });
            dispatch(userSetCurrentProcuration(10));
            await updateTeachrData(updatedTeachr, dispatch, setSiretVerifying, () => nextStep(2));
        } catch (error) {
            console.error('Erreur lors de la mise à jour pour la coopérative :', error);
        }
    };

    const shouldShowSiretForm = (!isAccordionOpen && selectedBusinessTypeIndex !== undefined && hasSelectedBusinessType)
    const shouldShowNoBusinessScreen = !isAccordionOpen && hasNoBusiness && !isBrandActive(ENV_VERIFIED_TRAINER);
    return {
        selectedBusinessTypeIndex,
        isProcessingProcuration,
        businessTypeOptions,
        handleProcurationForAutoEntrepreneur,
        handleSelectBusinessType,
        handleGoBack,
        handleVerifySiret,
        handleProcurationSapClick,
        handleGoToProcuration,
        handleCooperative,
        siret,
        setSiret,
        errorSiret,
        setErrorSiret,
        siretApiError,
        setSiretApiError,
        siretVerified,
        setSiretVerified,
        siretVerifying,
        homeCoursesAnswer,
        setHomeCoursesAnswer,
        showApeSuccess,
        setShowApeSuccess,
        showApeFailed,
        setShowApeFailed,
        shouldShowSiretForm,
        shouldShowNoBusinessScreen,
    };
};

// Composant principal
const FirstCompletProfil = () => {
    const { brandConfig } = useTailwindBrand();
    const { nextStep, teachr, setState } = useProfilContext();

    const {
        selectedBusinessTypeIndex,
        isProcessingProcuration,
        businessTypeOptions,
        handleProcurationForAutoEntrepreneur,
        handleSelectBusinessType,
        handleGoBack,
        handleVerifySiret,
        handleProcurationSapClick,
        handleGoToProcuration,
        handleCooperative,
        siret,
        setSiret,
        errorSiret,
        setErrorSiret,
        siretApiError,
        setSiretApiError,
        siretVerified,
        setSiretVerified,
        siretVerifying,
        homeCoursesAnswer,
        setHomeCoursesAnswer,
        showApeSuccess,
        setShowApeSuccess,
        showApeFailed,
        setShowApeFailed,
        shouldShowSiretForm,
        shouldShowNoBusinessScreen,
    } = useBusinessTypeSelection(teachr, nextStep, setState);

    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';



    const sectionBoxSx = {
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
        mt: 2,
    };

    return (
        <Settings
            title="Statut juridique"
            subtitle="Configurez votre statut et vérifiez votre numéro SIRET pour activer toutes les fonctionnalités."
            icon={<AccountBalanceIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            <Grid item xs={12} sm={12} md={12} lg={12}>
                {/* Sélecteur de type d'entreprise */}
                <Box sx={{ ...sectionBoxSx, mt: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: `${primaryHex}1a`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <AccountBalanceIcon sx={{ color: primaryHex, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                Forme juridique
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                Sélectionnez le statut correspondant à votre activité
                            </Typography>
                        </Box>
                    </Box>
                    <FormSelect
                        callback={handleSelectBusinessType}
                        required={true}
                        label="Type d'entreprise"
                        placeholder="Sélectionner votre type d'entreprise"
                        data={businessTypeOptions}
                        defaultValue={selectedBusinessTypeIndex !== undefined ? selectedBusinessTypeIndex + 1 : 0}
                    />
                </Box>

                {/* Formulaire SIRET */}
                {shouldShowSiretForm && (
                    <Box sx={sectionBoxSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: siretVerified ? '#dcfce7' : `${primaryHex}1a`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    transition: 'background-color 0.3s',
                                }}
                            >
                                {siretVerified
                                    ? <VerifiedIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                    : <VerifiedIcon sx={{ color: primaryHex, fontSize: 20 }} />
                                }
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                    Numéro SIRET
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                    Identifiant unique à 14 chiffres de votre établissement
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <FormInput
                                label="Numéro SIRET"
                                type="text"
                                placeholder="Ex : 91234567800016"
                                value={siret}
                                error={siretApiError ?? (errorSiret ? ValidateSiret(siret).message : undefined)}
                                onChange={(e) => {
                                    setSiret(e.target.value);
                                    if (e.target.value) {
                                        if (setState) {
                                            setState(prev => ({ ...prev, hasSiret: true }));
                                        }
                                    }
                                    setErrorSiret(false);
                                    setSiretApiError(undefined);
                                    setSiretVerified(false);
                                    setHomeCoursesAnswer(null);
                                    setShowApeSuccess(false);
                                    setShowApeFailed(false);
                                }}
                            />

                            <button
                                onClick={handleVerifySiret}
                                disabled={siretVerifying || !siret}
                                className="w-full py-2.5 px-4 rounded-md font-semibold text-sm transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed text-white flex items-center justify-center gap-2"
                                style={siretVerifying || !siret ? undefined : { backgroundColor: siretVerified ? '#16a34a' : primaryHex }}
                            >
                                {siretVerifying ? (
                                    <>
                                        <CircularProgress size={14} sx={{ color: 'white' }} />
                                        Vérification en cours...
                                    </>
                                ) : siretVerified ? (
                                    <>
                                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                                        SIRET vérifié
                                    </>
                                ) : (
                                    'Valider le SIRET'
                                )}
                            </button>
                        </Box>

                        {/* Résultat de vérification */}
                        {siretVerified && (
                            <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid #bbf7d0', bgcolor: '#f0fdf4' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22 }} />
                                    <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#166534', fontFamily: brandConfig.fontFamily }}>
                                        {homeCoursesAnswer === null
                                            ? 'SIRET valide,  répondez à la question ci-dessous'
                                            : showApeSuccess
                                                ? 'Code APE conforme au service à la personne'
                                                : showApeFailed
                                                    ? 'Code APE non reconnu'
                                                    : 'Prestations multiples détectées'}
                                    </Typography>
                                </Box>

                                {homeCoursesAnswer === null && (
                                    <div className="flex flex-col gap-y-3">
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 1, bgcolor: 'white', border: '1px solid #d1fae5' }}>
                                            <HomeIcon sx={{ color: '#16a34a', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                            <Typography variant="body2" fontWeight="600" sx={{ color: '#166534', fontFamily: brandConfig.fontFamily }}>
                                                Facturez-vous <strong>uniquement</strong> des {getActivePrestationBrandConfig("s")} {isBrandActive(ENV_DECLARONS) ? "relevant exclusivement du domaine des Services à la Personne" : 'à domicile'} ?  <br />
                                                <a href="https://www.servicesalapersonne.gouv.fr/tout-savoir-sur-les-services-la-personne/les-26-activites-de-services-la-personne" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                                    Consulter la liste des prestations relevant exclusivement du domaine des Services à la Personne
                                                </a>
                                            </Typography>
                                        </Box>
                                        <div className="flex gap-3">
                                            <button
                                                onClick={handleProcurationSapClick}
                                                className="flex-1 py-2.5 px-4 rounded-md font-semibold text-sm border-2 hover:opacity-90 transition-all text-white"
                                                style={{ backgroundColor: primaryHex, borderColor: primaryHex }}
                                            >
                                                Oui, uniquement à domicile
                                            </button>
                                            <button
                                                onClick={() => setHomeCoursesAnswer(false)}
                                                className="flex-1 py-2.5 px-4 rounded-md font-semibold text-sm border-2 border-gray-300 hover:bg-gray-50 transition-all text-gray-700"
                                            >
                                                Non, d'autres prestations
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {showApeSuccess && (
                                    <div className="flex flex-col gap-y-3">
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 1, bgcolor: 'white', border: '1px solid #d1fae5' }}>
                                            <VerifiedIcon sx={{ color: '#16a34a', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                            <Typography variant="body2" sx={{ color: '#166534', fontFamily: brandConfig.fontFamily }}>
                                                Votre code APE correspond aux activités de service à la personne. Vous pouvez passer à l'étape suivante pour effectuer votre déclaration NOVA.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <ButtonField
                                                label="Précédent"
                                                variant="outlined"
                                                className="w-full font-bold lg:w-auto"
                                                onClick={() => {
                                                    setHomeCoursesAnswer(null);
                                                    setShowApeSuccess(false);
                                                }}
                                            />
                                            <ButtonField
                                                label="Suivant"
                                                variant="contained"
                                                className="w-full font-bold lg:w-auto"
                                                onClick={handleGoToProcuration}
                                            />
                                        </Box>
                                    </div>
                                )}

                                {showApeFailed && (
                                    <div className="flex flex-col gap-y-4">
                                        <Box sx={{ p: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <WarningIcon sx={{ color: '#d97706', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                                <Typography variant="body2" sx={{ color: '#92400e', fontFamily: brandConfig.fontFamily }}>
                                                    Votre code APE ne correspond pas aux activités SAP reconnues (<strong>8559B</strong> ou <strong>8552Z</strong>). Pour facturer des cours à domicile avec déduction fiscale, un code APE conforme est nécessaire.
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={URSSAF_MANAGE_BUSINESS_URL}
                                            className="inline-flex items-center gap-1 font-bold text-sm underline hover:opacity-80 transition-opacity"
                                            style={{ color: primaryHex }}
                                        >
                                            <SwapHorizIcon sx={{ fontSize: 16 }} />
                                            Changer mon code APE
                                        </a>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'grey.300' }} />
                                            <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ px: 1 }}>ou</Typography>
                                            <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'grey.300' }} />
                                        </Box>

                                        <Box sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                                                <GroupsIcon sx={{ color: '#16a34a', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                                <Typography variant="body2" sx={{ color: '#166534', fontFamily: brandConfig.fontFamily }}>
                                                    Continuez sans changer de code APE en adhérant à notre coopérative <strong>Verified Trainer SAP</strong>. Vous pourrez facturer l'ensemble de vos prestations.
                                                </Typography>
                                            </Box>

                                            <ButtonField
                                                label="Adhérer à la coopérative"
                                                variant="contained"
                                                className="w-full font-bold"
                                                startIcon={<GroupsIcon />}
                                                onClick={handleCooperative}
                                            />
                                        </Box>
                                    </div>
                                )}

                                {homeCoursesAnswer === false && !showApeSuccess && !showApeFailed && (
                                    <div className="flex flex-col gap-y-3">
                                        <Box sx={{ p: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <GroupsIcon sx={{ color: '#2563eb', fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                                <Typography variant="body2" sx={{ color: '#1e40af', fontFamily: brandConfig.fontFamily }}>
                                                    Vous proposez des services au-delà des cours à domicile. En adhérant à notre coopérative {isBrandActive(ENV_VERIFIED_TRAINER) ? "" : 'partenaire'} <strong>Verified Trainer SAP</strong>, vous facturerez l'ensemble de vos prestations sans contrainte de code APE.
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            {!teachr?.scoop && (
                                                <ButtonField
                                                    label="Précédent"
                                                    variant="outlined"
                                                    className="w-full font-bold lg:w-auto"
                                                    onClick={() => {
                                                        setHomeCoursesAnswer(null);
                                                        setShowApeSuccess(false);
                                                    }}
                                                />
                                            )}
                                            <ButtonField
                                                label="Adhérer à la coopérative"
                                                variant="contained"
                                                className="w-full font-bold lg:w-auto"
                                                startIcon={<GroupsIcon />}
                                                onClick={handleCooperative}
                                            />
                                        </Box>
                                    </div>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Écran "Pas de société" */}
                {shouldShowNoBusinessScreen && (
                    <Box sx={{ ...sectionBoxSx, bgcolor: '#fffbeb', borderColor: '#fde68a' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    bgcolor: '#fef3c7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <InfoIcon sx={{ color: '#d97706', fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#92400e', fontFamily: brandConfig.fontFamily }}>
                                    Création d'une auto-entreprise requise
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#b45309', fontFamily: brandConfig.fontFamily, mt: 0.5 }}>
                                    Un statut auto-entrepreneur est nécessaire pour accéder à toutes les fonctionnalités de la plateforme.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ p: 2, bgcolor: 'white', border: '1px solid #fde68a', borderRadius: 1.5 }}>
                                <Typography variant="body2" fontWeight="600" sx={{ color: '#92400e', fontFamily: brandConfig.fontFamily, mb: 1 }}>
                                    Créez votre statut auto-entrepreneur en quelques minutes sur le site officiel de l'INPI.
                                </Typography>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={URSSAF_AUTO_ENTREPRENEUR_URL}
                                    className="inline-flex items-center gap-1 font-bold text-sm underline hover:opacity-80 transition-opacity"
                                    style={{ color: primaryHex }}
                                >
                                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                                    Faire mes démarches sur procedures.inpi.fr
                                </a>
                            </Box>

                            <Box sx={{ p: 2, bgcolor: 'white', border: '1px solid #fde68a', borderRadius: 1.5 }}>
                                <Typography variant="body2" fontWeight="600" sx={{ color: '#92400e', fontFamily: brandConfig.fontFamily, mb: 4 }}>
                                    Vous préférez déléguer ? Envoyez-nous une <strong>procuration</strong> et nous nous occupons de tout à votre place.
                                </Typography>
                                <ButtonField
                                    label="Envoyer une procuration"
                                    variant="contained"
                                    className="font-bold w-full mt-4"
                                    onClick={handleProcurationForAutoEntrepreneur}
                                    disabled={isProcessingProcuration}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                            <ButtonField
                                label="Précédent"
                                variant="outlined"
                                className="font-bold"
                                onClick={handleGoBack}
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </Settings>
    );
};

export default FirstCompletProfil;
